import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { closeDialog } from "../../components/dialog/dialogSlice";
import AppDialog from "../../components/dialog/Dialog";
import { Switch } from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";
import { axiosApiInstance } from "../../app/axios";
import { API_URL } from "../../app/config";
import { ErrorToast, SuccessToast } from "../../components/toast/Toasts";
import { LoadingPanel } from "../../components/layout/Loading";
import { FaUsers, FaHotel, FaPlane, FaPassport } from "react-icons/fa";
import "./SoftwareSettings.scss";
import IconComponent from "../../components/common/IconComponent";

const SoftwareSettingsDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const isOpen = dialogName === "softwareSettings";

  const [settings, setSettings] = useState({
    Crm: false,
    Hotel: false,
    Flight: false,
    Visa: false,
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const fetchSettings = async () => {
        setLoading(true);
        try {
          const response = await axiosApiInstance.get(
            `${API_URL}/SoftwareSettings/GetSoftwareSettings`,
          );
          const data = response.data?.Data || response.data;
          if (data) {
            setSettings({
              Crm: data.Crm ?? false,
              Hotel: data.Hotel ?? false,
              Flight: data.Flight ?? false,
              Visa: data.Visa ?? false,
            });
          }
        } catch (error: any) {
          console.error("Error fetching software settings:", error);
          ErrorToast(
            error?.response?.data?.Details ||
              "Failed to fetch software settings",
          );
        } finally {
          setLoading(false);
        }
      };
      fetchSettings();
    }
  }, [isOpen]);

  const handleToggle = (key: keyof typeof settings) => {
    setSettings((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/SoftwareSettings/InsertSoftwareSettings`,
        settings,
      );
      SuccessToast(
        response.data?.Details || "Software settings updated successfully",
      );
      dispatch(closeDialog());
    } catch (error: any) {
      console.error("Error saving software settings:", error);
      ErrorToast(
        error?.response?.data?.Details || "Failed to save software settings",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    dispatch(closeDialog());
  };

  return (
    <>
      {isOpen && (
        <AppDialog className="software-settings-dialog">
          <div className="settings-container">
            {(loading || saving) && <LoadingPanel />}
            <h5 className="settings-title">Software Settings</h5>
            <form onSubmit={handleSubmit}>
              <div className="settings-grid">
                {/* CRM Card */}
                <div className="settings-card">
                  <div>
                    <div className="card-header">
                      <div className="icon-wrapper crm">
                        <IconComponent icon={FaUsers} />
                      </div>
                      <h6 className="card-title">CRM Module</h6>
                      <Switch
                        checked={settings.Crm}
                        onChange={() => handleToggle("Crm")}
                        size="small"
                        className="setting-switch"
                      />
                    </div>
                    {/* <div className="card-body">
                      <p className="card-description">
                        Manage customer relations, leads, inquiries, and
                        follow-ups.
                      </p>
                    </div> */}
                  </div>
                  {/* <div className="card-action">
                    <Switch
                      checked={settings.Crm}
                      onChange={() => handleToggle("Crm")}
                      size="medium"
                    />
                  </div> */}
                </div>

                {/* Hotel Card */}
                <div className="settings-card">
                  <div>
                    <div className="card-header">
                      <div className="icon-wrapper hotel">
                        <IconComponent icon={FaHotel} />
                      </div>
                      <h6 className="card-title">Hotel Module</h6>
                      <Switch
                        checked={settings.Hotel}
                        onChange={() => handleToggle("Hotel")}
                        size="small"
                        className="setting-switch"
                      />
                    </div>
                    {/* <div className="card-body">
                      <p className="card-description">
                        Control hotel bookings, rates, inventory, and
                        accommodation details.
                      </p>
                    </div> */}
                  </div>
                  {/* <div className="card-action">
                  </div> */}
                </div>

                {/* Flight Card */}
                <div className="settings-card">
                  <div>
                    <div className="card-header">
                      <div className="icon-wrapper flight">
                        <IconComponent icon={FaPlane} />
                      </div>
                      <h6 className="card-title">Flight Module</h6>
                      <Switch
                        checked={settings.Flight}
                        onChange={() => handleToggle("Flight")}
                        size="small"
                        className="setting-switch"
                      />
                    </div>
                    {/* <div className="card-body">
                      <p className="card-description">
                        Manage flight reservations, class blockings, routes, and
                        tickets.
                      </p>
                    </div> */}
                  </div>
                  {/* <div className="card-action">
                  </div> */}
                </div>

                {/* Visa Card */}
                <div className="settings-card">
                  <div>
                    <div className="card-header">
                      <div className="icon-wrapper visa">
                        <IconComponent icon={FaPassport} />
                      </div>
                      <h6 className="card-title">Visa Module</h6>
                      <Switch
                        checked={settings.Visa}
                        onChange={() => handleToggle("Visa")}
                        size="small"
                        className="setting-switch"
                      />
                    </div>
                    {/* <div className="card-body">
                      <p className="card-description">
                        Process visa applications, requirements, entries, and
                        documentation.
                      </p>
                    </div> */}
                  </div>
                  {/* <div className="card-action">                   
                  </div> */}
                </div>
              </div>

              <div className="dialog-actions">
                <Button
                  type="button"
                  fillMode="outline"
                  themeColor="primary"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  fillMode="solid"
                  themeColor="primary"
                  disabled={loading || saving}
                >
                  Save Settings
                </Button>
              </div>
            </form>
          </div>
        </AppDialog>
      )}
    </>
  );
};

export default SoftwareSettingsDialog;
